import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {urlParams} from 'utils/request';
import useStarfishAPI from '../hooks/useStarfishAPI';
import TextHeader from "./TextHeader";
import css from "./CourseSelection.css";
import useError from "../hooks/useError";
import RadioCardOption from './RadioCardOption'
import {Card} from "semantic-ui-react";
import ContinueButton from "./ContinueButton";
import SubmitStudentAPI from "./SubmitStudentAPI";

function CourseSelection(props) {

  const [{data: courseData, success: courseSuccess, error: courseError, pending: coursePending}, callKioskForCourses] = useStarfishAPI();
  const [{success: submitStudentSuccess, error: submitStudentError}, {onSubmitStudentApi}] = SubmitStudentAPI(props.setWaitingRoomQueueId);
  const [course, setCourse] = useState(props.course);
  useError(courseError, props.setError);
  useError(submitStudentError, props.setError);

  const getUrl = () => {
    if (props.instructorId) {
      return `../kiosk/getGroupSessionCoursesInCommon.html?${urlParams({
        parentId: props.instructorId,
        childId: props.studentId,
        reasonId: props.reasonId
      })}`;
    }
    return `../kiosk/getCoursesForStudent.html?${urlParams({studentId: props.studentId})}`;
  };

  const filterCourseSelections = (courseSelections) => {
    if (!props.supplementalInstruction) return courseSelections;
    const { supplementalInstructionType, groupSessionCourseRestrictions } = props;
    return courseSelections.filter((courseSelection) => {
      if (supplementalInstructionType === 'SECTION') {
        return groupSessionCourseRestrictions.some(({sectionId}) => sectionId === courseSelection.sectionId);
      }
      if (supplementalInstructionType === 'COURSE') {
        return groupSessionCourseRestrictions.some(({courseId}) => courseId === courseSelection.courseId);
      }
      return true;
    });
  };

  if (submitStudentSuccess) {
    props.goToConfirmationPage();
  }

  const getCourses = () => {
    return callKioskForCourses(getUrl(), {
      method: 'GET',
    });
  };

  useEffect(() => {
    getCourses();
  }, [props.instructorId, props.studentId, props.reasonId]);

  const selectCourse = () => {
    if (props.showMoreInformationPage) {
      props.selectCourse(course);
      return;
    }

    const params = urlParams({
      studentId: props.studentId,
      serviceId: props.serviceId,
      reasonId: props.reasonId,
      kioskId: props.kioskId,
      courseId: course.id,
      groupSessionId: props.groupSessionId,
    }, false);
    onSubmitStudentApi(params);
  };

  // If supplemental instruction is enabled, disable continue button until a valid course is selected
  const isButtonDisabled = () => {
    if ( !props.supplementalInstruction ) return false;
    if ( !course ) return true;
    return !course.id;
  };

  const getCoursesCardOptions = () => {
    return (
        <div>
          <Card.Group role="radiogroup" aria-labelledby="courseSelection">
            {!props.supplementalInstruction && <RadioCardOption
                label={'No course'}
                radioGroupName={'courseSelection'}
                onSelect={() => {
                  setCourse({});
                }}
                isChecked={!(course && course.id)}
            />}
            {filterCourseSelections(courseData.result).map(courseItem => {
              return (
                  <RadioCardOption
                      key={courseItem.id}
                      label={courseItem.fullName}
                      radioGroupName={'courseSelection'}
                      onSelect={() => {
                        setCourse(courseItem);
                      }}
                      isChecked={!!course && courseItem.id === course.id}
                  />
              );
            })}
          </Card.Group>
          <ContinueButton onClick={selectCourse} disabled={isButtonDisabled()}/>
        </div>
    );
  };

  if (coursePending) {
    return <div aria-label='Loading courses...'>Loading courses...</div>;
  } else if (!courseSuccess) {
    return <div>Unable to fetch courses</div>;
  }

  return (
      <div>
        <div className={css['content']}>
          <div className={css['header']}>
            <TextHeader
                id="courseSelection"
                upperText={props.relatedToCoursesPageHeaderOne}
                lowerText={props.relatedToCoursesPageHeaderTwo}/>
          </div>
          {getCoursesCardOptions()}
        </div>
      </div>
  );

}

CourseSelection.propTypes = {
  studentId: PropTypes.number.isRequired,
  instructorId: PropTypes.number,
  reasonId: PropTypes.number,
  course: PropTypes.object,
  selectCourse: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
  relatedToCoursesPageHeaderOne: PropTypes.string.isRequired,
  relatedToCoursesPageHeaderTwo: PropTypes.string.isRequired,
  showMoreInformationPage: PropTypes.bool.isRequired,
  serviceId: PropTypes.number.isRequired,
  kioskId: PropTypes.number.isRequired,
  groupSessionId: PropTypes.number,
  groupSessionCourseRestrictions: PropTypes.arrayOf(PropTypes.object),
  supplementalInstruction: PropTypes.bool,
  supplementalInstructionType: PropTypes.string,
  goToConfirmationPage: PropTypes.func.isRequired,
  setWaitingRoomQueueId: PropTypes.func.isRequired,
};

CourseSelection.defaultProps = {
  relatedToCoursesPageHeaderOne: 'Here is a greeting text that is configurable.',
  relatedToCoursesPageHeaderTwo: 'Is this visit related to one of your courses?'
};

export default CourseSelection;
